import { useEffect } from 'react';
import { WalkthroughStep } from 'models/chatbot';
import { checkForExpectedText, getDisplayValue } from '../utils/checkExpectedText';
import { setupCheckboxListener } from '../handlers/checkboxHandler';
import { setupSelectRowListener } from '../handlers/selectRowHandler';
import { findSelectPortalMenus, findDatepickerPortal } from '../utils/selectPortal';

export const useWalkthroughEvents = (
  currentStep: WalkthroughStep | null,
  isActive: boolean,
  onNextStep: () => void,
) => {
  useEffect(() => {
    if (!currentStep || !isActive) return;

    const targets = currentStep.uiTarget.split('|').map((t) => t.trim());
    if (targets.length === 0) return;

    const waitForParts = (currentStep.waitFor || '').split('|').map((w) => w.trim());
    const cleanupFns: (() => void)[] = [];

    const setupEventListeners = (targetElement: Element, waitForFull: string) => {
      const waitForType = waitForFull.startsWith('userSelect:') ? 'userSelect' : waitForFull;

      switch (waitForType) {
        case 'userClick': {
          const clickHandler = () => onNextStep();
          targetElement.addEventListener('click', clickHandler);
          return () => targetElement.removeEventListener('click', clickHandler);
        }

        case 'userDoubleClick': {
          const docClickHandler = (e: Event) => {
            if (!targetElement.contains(e.target as Node)) return;
            if ((e as MouseEvent).detail >= 2) onNextStep();
          };
          document.addEventListener('click', docClickHandler, true);
          return () => document.removeEventListener('click', docClickHandler, true);
        }

        case 'userSelect': {
          const hasExpectedValue = waitForFull.includes(':');
          const expectedValue = hasExpectedValue ? waitForFull.replace('userSelect:', '') : '';
          const initialValue = getDisplayValue(targetElement);
          let advanced = false;

          const checkValueChanged = () => {
            if (advanced) return;
            if (targetElement.querySelector('.dropdown-content')) return;
            if (hasExpectedValue) {
              checkForExpectedText(targetElement, expectedValue, () => {
                advanced = true;
                onNextStep();
              });
            } else {
              const currentValue = getDisplayValue(targetElement);
              if (currentValue.length > 0 && currentValue !== initialValue) {
                advanced = true;
                onNextStep();
              }
            }
          };

          const debouncedCheck = () => setTimeout(checkValueChanged, 50);

          const docClickHandler = (e: Event) => {
            const clickTarget = e.target as Node;
            if (targetElement.contains(clickTarget)) return;
            const portalMenus = findSelectPortalMenus(targetElement);
            const datepicker = findDatepickerPortal(targetElement);
            const allPortals = [...portalMenus, datepicker].filter(Boolean) as Element[];
            if (allPortals.some((portal) => portal.contains(clickTarget))) {
              debouncedCheck();
            }
          };

          document.addEventListener('click', docClickHandler, true);

          const multiSelectBlurHandler = (e: Event) => {
            const blurredEl = (e as FocusEvent).target as Element;
            const newFocus = (e as FocusEvent).relatedTarget as Element | null;

            if (!blurredEl.closest?.('.dropdown-content')) return;
            if (newFocus?.closest?.('.dropdown-content')) return;

            setTimeout(() => {
              if (!advanced && !targetElement.querySelector('.dropdown-content')) {
                advanced = true;
                onNextStep();
              }
            }, 100);
          };

          targetElement.addEventListener('blur', multiSelectBlurHandler, true);

          const observer = new MutationObserver(debouncedCheck);
          observer.observe(targetElement, {
            childList: true,
            subtree: true,
            characterData: true,
          });

          return () => {
            document.removeEventListener('click', docClickHandler, true);
            targetElement.removeEventListener('blur', multiSelectBlurHandler, true);
            observer.disconnect();
          };
        }

        case 'userBlur': {
          const portalIds = ['react-select-portal', 'datepicker-portal', 'multifilter-portal'];

          const blurHandler = (e: Event) => {
            const focusEvent = e as FocusEvent;
            const blurredEl = focusEvent.target as HTMLElement;

            if (blurredEl.tagName !== 'INPUT' && blurredEl.tagName !== 'TEXTAREA') return;

            const newFocusTarget = focusEvent.relatedTarget as HTMLElement | null;
            if (newFocusTarget) {
              if (targetElement.contains(newFocusTarget)) return;
              if (portalIds.some((id) => document.getElementById(id)?.contains(newFocusTarget))) return;
            }

            setTimeout(() => {
              const activeEl = document.activeElement as HTMLElement | null;
              if (activeEl && targetElement.contains(activeEl)) return;
              if (activeEl && portalIds.some((id) => document.getElementById(id)?.contains(activeEl))) return;

              const value = (blurredEl as HTMLInputElement | HTMLTextAreaElement).value || '';
              if (value.trim().length > 0) onNextStep();
            }, 0);
          };
          targetElement.addEventListener('blur', blurHandler, true);
          return () => targetElement.removeEventListener('blur', blurHandler, true);
        }

        case 'userCheckbox':
          return setupCheckboxListener(targetElement, onNextStep);

        case 'userSelectRow':
          return setupSelectRowListener(targetElement, onNextStep);

        default:
          return undefined;
      }
    };

    const allElements: Element[] = [];
    for (const target of targets) {
      const selector = `[data-walkthrough="${target}"]`;
      document.querySelectorAll(selector).forEach((el) => allElements.push(el));
    }

    if (allElements.length > 0) {
      allElements.forEach((element) => {
        waitForParts.forEach((waitForFull) => {
          const cleanupFn = setupEventListeners(element, waitForFull);
          if (cleanupFn) cleanupFns.push(cleanupFn);
        });
      });
    } else {
      const elementObserver = new MutationObserver(() => {
        const newElements: Element[] = [];
        for (const target of targets) {
          const selector = `[data-walkthrough="${target}"]`;
          document.querySelectorAll(selector).forEach((el) => {
            if (!newElements.includes(el)) newElements.push(el);
          });
        }

        if (newElements.length > 0) {
          elementObserver.disconnect();
          newElements.forEach((element) => {
            waitForParts.forEach((waitForFull) => {
              const cleanupFn = setupEventListeners(element, waitForFull);
              if (cleanupFn) cleanupFns.push(cleanupFn);
            });
          });
        }
      });

      elementObserver.observe(document.body, { childList: true, subtree: true });
      return () => {
        elementObserver.disconnect();
        cleanupFns.forEach((fn) => fn());
      };
    }

    return () => cleanupFns.forEach((fn) => fn());
  }, [currentStep, isActive, onNextStep]);
};
